import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for checking module-level permissions
 * Uses the dynamic permissions from Access Management system
 * 
 * @returns {Object} Permission checking functions
 */
export const useModulePermissions = () => {
  const {
    modulePermissions,
    hasModulePermission,
    canViewModule,
    canEditModule,
    canCreateModule,
    canDeleteModule,
    isSuperAdmin,
    isCompanyAdmin,
    loading,
  } = useAuth();

  /**
   * Check if a specific module action is allowed
   * @param {string} moduleName - Name of the module (e.g., 'Invoices', 'Customers')
   * @param {string} action - Action type: 'view', 'edit', 'create', 'delete'
   * @returns {boolean} Whether the action is allowed
   */
  const checkModuleAccess = (moduleName, action = 'view') => {
    if (!moduleName) return false;
    
    // SuperAdmin and CompanyAdmin have full access
    if (isSuperAdmin || isCompanyAdmin) return true;
    
    const actionMap = {
      view: 'CanView',
      edit: 'CanEdit',
      create: 'CanCreate',
      delete: 'CanDelete',
    };
    
    const permissionType = actionMap[action.toLowerCase()] || 'CanView';
    return hasModulePermission(moduleName, permissionType);
  };

  /**
   * Check multiple module permissions at once
   * @param {string} moduleName - Name of the module
   * @returns {Object} Object with all permission flags
   */
  const getModulePermissions = (moduleName) => {
    if (!moduleName) {
      return {
        canView: false,
        canEdit: false,
        canCreate: false,
        canDelete: false,
      };
    }

    // SuperAdmin and CompanyAdmin have full access
    if (isSuperAdmin || isCompanyAdmin) {
      return {
        canView: true,
        canEdit: true,
        canCreate: true,
        canDelete: true,
      };
    }

    const perms = modulePermissions[moduleName] || {};
    return {
      canView: perms.CanView === true,
      canEdit: perms.CanEdit === true,
      canCreate: perms.CanCreate === true,
      canDelete: perms.CanDelete === true,
    };
  };

  /**
   * Check if user has any access to a module
   * @param {string} moduleName - Name of the module
   * @returns {boolean} True if user has any permission for the module
   */
  const hasAnyModuleAccess = (moduleName) => {
    if (isSuperAdmin || isCompanyAdmin) return true;
    
    const perms = getModulePermissions(moduleName);
    return perms.canView || perms.canEdit || perms.canCreate || perms.canDelete;
  };

  return {
    // Individual permission checks
    canViewModule,
    canEditModule,
    canCreateModule,
    canDeleteModule,
    
    // Utility functions
    checkModuleAccess,
    getModulePermissions,
    hasAnyModuleAccess,
    
    // Raw permissions data
    modulePermissions,
    
    // Loading state
    loading,
  };
};

export default useModulePermissions;
